"use client";

import { useEffect, useState } from "react";
import { ArrowUpRight, Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        <div className="h-4 w-20 bg-muted/60 animate-pulse rounded-md" />
        <div className="h-4 w-20 bg-muted/60 animate-pulse rounded-md" />
        <div className="h-4 w-20 bg-muted/60 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem data-index={i} key={item.id}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative font-default text-foreground uppercase text-xs tracking-[0.2em] py-2 px-0 bg-transparent hover:bg-transparent focus:bg-transparent group transition-colors duration-300">
                <span className="relative">
                  {item.label}
                  <span className="absolute left-0 -bottom-1 h-px w-0 bg-foreground transition-all duration-[400ms] ease-out group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-8 mt-12">
        <div className="h-8 w-40 bg-muted/60 animate-pulse rounded-md" />
        <div className="h-8 w-40 bg-muted/60 animate-pulse rounded-md" />
        <div className="h-8 w-40 bg-muted/60 animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-6 px-8 mt-12">
      {menuItems.map((item, i) => <Link data-index={i} key={item.id} to={item.href || "#"} onClick={onItemClick} className="font-default italic text-3xl text-foreground hover:text-primary transition-colors duration-300">
          {item.label}
        </Link>)}
    </nav>;
}
export default function MainNavigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="5178" id="main-navigation" className={`sticky top-0 z-50 w-full transition-all duration-500 ease-out bg-background text-foreground ${scrolled ? "bg-background/90 backdrop-blur-sm border-b border-border" : "bg-background border-b border-transparent"}`}>
      <div className={`mx-auto flex items-center justify-between px-6 md:px-12 lg:px-20 transition-all duration-500 ${scrolled ? "h-20" : "h-[88px]"}`}>
        {}
        <div className="hidden lg:grid grid-cols-3 items-center w-full">
          {}
          <div className="flex items-center justify-start">
            <MenuProvider menu_id="6" className="header-menu-desktop">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center justify-center">
            <Link to="/" className="inline-flex items-center">
              <WvcLogo className="h-7 w-auto" />
            </Link>
          </div>

          {}
          <div className="flex items-center justify-end gap-6">
            <Link to="/StartYourJourneyPage" className="font-default text-foreground uppercase text-xs tracking-[0.2em] hover:text-primary transition-colors duration-300">
              Programs
            </Link>
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-default uppercase text-xs tracking-[0.2em] px-8 h-12 shadow-md">
              <Link to="/StartYourJourneyPage">
                Start Training
                <ArrowUpRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>

        {}
        <div className="flex lg:hidden items-center justify-between w-full">
          {}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent" aria-label="Open menu">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-background border-r border-border w-full sm:max-w-md p-0 flex flex-col">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>

              {}
              <div className="relative w-full h-48 overflow-hidden bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700144/img/fitness_training_hero.png)]">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background/80" />
              </div>

              <div className="flex-1 flex flex-col">
                <MenuProvider menu_id="6" className="header-menu-mobile">
                  <MobileMenuConsumer onItemClick={() => setMobileOpen(false)} />
                </MenuProvider>

                <div className="mt-auto px-8 pb-10 pt-8 border-t border-border">
                  <Button asChild onClick={() => setMobileOpen(false)} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-default uppercase text-xs tracking-[0.2em] h-12">
                    <Link to="/StartYourJourneyPage">
                      Start Training
                      <ArrowUpRight className="ml-1 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>

          {}
          <Link to="/" className="inline-flex items-center">
            <WvcLogo className="h-6 w-auto" />
          </Link>

          {}
          <div className="w-9" />
        </div>
      </div>
    </section>;
}